/** @jsx jsx */
import { jsx } from '@emotion/react'
import { Typography, Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import apiChallenge from '@reducers/challenge/api'
import _ from 'lodash'
import React, { useMemo, useState } from 'react'
import { useAsyncFetch } from 'react-hooks-async-handlers'
import { useDispatch } from 'react-redux'
import { RendererStatusSplit } from 'react-renderer-status-split'
import Container from '../../components/Container'
import ReactionLoader from '../../components/ReactionLoader'
import { OpenSans } from '../../helpers/commonStyles'
import { ReduxDispatch } from '../../typings/ReduxDispatch'
import BreathIcon from './assets/breathing-challenge.png'
import PhotoIcon from './assets/photo-challenge.png'
import MediationIcon from './assets/mediation-challenge.png'
import RunIcon from './assets/run-challenge.png'
import ScoreIcon from './assets/score-challenge.png'
import SquatIcon from './assets/squat-challenge.png'
import StepIcon from './assets/step-challenge.png'
import PlankIcon from './assets/plank.png'
import Challenge from './groups/Challenge'
import ChallengeItem from './groups/ChallengeItem'

const useStyles = makeStyles(
  {
    rootContainer: {
      padding: '50px 0',
      fontFamily: OpenSans,
      position: 'relative',
      margin: '0 30px',
    },

    itemsContainer: {
      padding: 30,
    },

    programsImage: {
      maxWidth: 260,
    },
    freeTrialContainer: {
      padding: '50px 50px 40px',
    },
    break: {
      backgroundColor: '#E2DFDF',
      height: 1,
      margin: '26px 0',
    },
    plank: {
      backgroundColor: '#E2DFDF',
      height: 1,
      margin: '26px 0',
    },
  },
  {
    classNamePrefix: 'Groups',
  },
)

const comingSoonChallenges = [
  {
    title: 'Score prediction',
    description:
      "Who's up for guessing the next Eurovision champion or World Cup Match results? Kick off a fun score prediction challenge, where members earn points for their predictions!",
    icon: ScoreIcon,
    comingSoon: true,
    type: 'score-predication',
  },
  {
    title: 'Meditation Club',
    description:
      'Create a team support club to inspire each other in meditation and compare meditation minutes. Including an in-app range of meditation options',
    icon: MediationIcon,
    comingSoon: true,
    type: 'mediation',
  },
  {
    title: 'Pictures challenge',
    description:
      "Members will receive a daily prompt, take a picture in response, and anonymously rate others' responses. This will create a collage of fun and happy pictures",
    icon: PhotoIcon,
    comingSoon: true,
    type: 'pictures',
  },
  {
    title: 'Run Crew',
    description:
      'Launch the Workplace Run Crew! Runners can select a training program or log their runs to be showcased on the crew leaderboard, fostering mutual support and motivation',
    icon: RunIcon,
    comingSoon: true,
    type: 'run-crew',
  },
]

function Groups(props) {
  const classes = useStyles()
  const dispatch = useDispatch<ReduxDispatch>()
  const [challenges, setChallenges] = useState<any[]>([])

  const dataFetchChallengesAction = useAsyncFetch(async () => {
    const res = await apiChallenge.getChallenges()
    setChallenges(res)
  })

  const hasSquatChallenge = useMemo(() => {
    return challenges?.find((c) => c.type === 'squats')
  }, [challenges])

  const hasBreathChallenge = useMemo(() => {
    return challenges?.find((c) => c.type === 'breath')
  }, [challenges])

  const hasPlankChallenge = useMemo(() => {
    return challenges?.find((c) => c.type === 'plank')
  }, [challenges])

  const hasStepsChallenge = useMemo(() => {
    return challenges?.find((c) => c.type === 'steps')
  }, [challenges])

  return (
    <Container>
      <RendererStatusSplit
        statuses={dataFetchChallengesAction}
        isEmpty={_.isEmpty(challenges)}
        renderLoading={() => <ReactionLoader marginTop={40} />}
        render={() => (
          <React.Fragment>
            {challenges?.length ? (
              <React.Fragment>
                <Typography variant="h1" mb={2}>
                  Active team activities
                </Typography>
                <Grid container className={classes.itemsContainer} gap={4}>
                  {_.map(challenges, (c) => (
                    <Challenge key={c.id} challenge={c} />
                  ))}
                </Grid>
                <div className={classes.break} />
              </React.Fragment>
            ) : null}
            <Typography variant="h1" mb={2}>
              Select team activities
            </Typography>
            <Grid container className={classes.itemsContainer}>
              {!hasStepsChallenge && (
                <ChallengeItem
                  title="Steps challenge"
                  type="steps"
                  description="Improve employee wellbeing, boost morale, and increase the sense of belonging at your company with the world's #1 workplace step challenge."
                  icon={StepIcon}
                />
              )}
              {!hasSquatChallenge && (
                <ChallengeItem
                  title="Squat challenge"
                  type="squats"
                  description="Empower your team to compete in daily average squats, automatically counted using AI computer vision technology."
                  icon={SquatIcon}
                />
              )}
              {!hasBreathChallenge && (
                <ChallengeItem
                  title="Breath challenge"
                  type="breath"
                  description="Members will be motivated to incorporate brief breathing breaks into their day, earning points for achieving their break goals. This aids in cultivating a relaxation routine."
                  icon={BreathIcon}
                />
              )}
              {!hasPlankChallenge && (
                <ChallengeItem
                  title="Plank challenge"
                  type="plank"
                  description="The workplace plank challenge app is designed to make fitness both fun and competitive. Using innovative AI motion tracking technology, the app not only records your daily plank times accurately but also offers personalized coaching to ensure members are planking correctly."
                  icon={PlankIcon}
                />
              )}
              {comingSoonChallenges.map((c, i) => (
                <ChallengeItem {...c} key={`coming-soon-challenge-${i}`} />
              ))}
            </Grid>
          </React.Fragment>
        )}
      />
    </Container>
  )
}

export const GROUP_LIST_URL = '/groups'

export default Groups
